import { useState, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Project, SavedRig, SharedProject } from '@/types/project';
import { storage } from '@/services/storage';
import { dbStorage } from '@/services/dbStorage';
import { useAuth } from '@/contexts/AuthContext';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { trackEvent } from '@/lib/analytics';

export type CreateProjectResult = 
  | { success: true; project: Project }
  | { success: false; reason: 'project-limit' };

export type AddRigResult = 
  | { success: true; rig: SavedRig }
  | { success: false; reason: 'rig-limit' };

async function fetchProjects(userId: string | null): Promise<{ projects: Project[]; sharedProjects: SharedProject[] }> {
  try {
    if (userId) {
      const dbProjects = await dbStorage.getProjects(userId);
      const dbSharedProjects = await dbStorage.getSharedProjects(userId);
      
      // Migrate localStorage projects on first login
      try {
        const localProjects = storage.getProjects();
        if (localProjects.length > 0 && dbProjects.length === 0) {
          for (const localProject of localProjects) {
            const newProject = await dbStorage.createProject(userId, localProject.name);
            if (newProject) {
              for (const rig of localProject.rigs) {
                await dbStorage.addRig(newProject.id, {
                  name: rig.name,
                  camera: rig.camera,
                  accessories: rig.accessories,
                  rigStyle: rig.rigStyle,
                  autoAddedCables: rig.autoAddedCables,
                });
              }
            }
          }
          storage.saveProjects([]);
          const migratedProjects = await dbStorage.getProjects(userId);
          return { projects: migratedProjects, sharedProjects: dbSharedProjects };
        }
      } catch (migrationError) {
        console.error('Error migrating local projects:', migrationError);
        // Continue with existing db projects if migration fails
      }
      
      return { projects: dbProjects, sharedProjects: dbSharedProjects };
    } else {
      const localProjects = storage.getProjects();
      return { projects: localProjects ?? [], sharedProjects: [] };
    }
  } catch (error) {
    console.error('Error fetching projects:', error);
    // Return empty arrays on error to prevent crash
    return { projects: [], sharedProjects: [] };
  }
}

import { CACHE_KEYS, isCacheValid } from '@/lib/cacheUtils';

// Cache configuration for projects
const PROJECTS_CACHE_KEY = CACHE_KEYS.PROJECTS;
const PROJECTS_CACHE_TTL = 1000 * 60 * 60; // 1 hour

interface CachedProjects {
  projects: Project[];
  sharedProjects: SharedProject[];
  userId: string;
  timestamp: number;
}

export function clearProjectsCache(): void {
  try {
    localStorage.removeItem(PROJECTS_CACHE_KEY);
  } catch (e) {
    // Ignore storage errors
  }
}

function getCachedProjects(userId: string): { projects: Project[]; sharedProjects: SharedProject[] } | null {
  try {
    const cached = localStorage.getItem(PROJECTS_CACHE_KEY);
    if (cached) {
      const parsed: CachedProjects = JSON.parse(cached);
      if (parsed.userId === userId && Date.now() - parsed.timestamp < PROJECTS_CACHE_TTL) {
        return { projects: parsed.projects, sharedProjects: parsed.sharedProjects };
      }
    }
  } catch (e) {
    console.error('Failed to load cached projects:', e);
  }
  return null;
}

function saveCachedProjects(userId: string, projects: Project[], sharedProjects: SharedProject[]): void {
  try {
    const cached: CachedProjects = { projects, sharedProjects, userId, timestamp: Date.now() };
    localStorage.setItem(PROJECTS_CACHE_KEY, JSON.stringify(cached));
  } catch (e) {
    console.error('Failed to cache projects:', e);
  }
}

export function useProjects(initialProjectId?: string) {
  const queryClient = useQueryClient();
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const { user } = useAuth();
  const { canCreateProject, canCreateRig, maxProjects, maxRigsPerProject, refreshUsage } = usePlanLimits();

  const userId = user?.id ?? 'guest';
  const cachedData = getCachedProjects(userId);

  // Use React Query for caching projects data
  const { data, isLoading: queryLoading, refetch } = useQuery({
    queryKey: ['projects', userId],
    queryFn: async () => {
      const result = await fetchProjects(user?.id ?? null);
      // Save to localStorage cache on successful fetch
      if (user?.id) {
        saveCachedProjects(user.id, result.projects, result.sharedProjects);
      }
      return result;
    },
    initialData: cachedData ?? undefined,
    staleTime: 1000 * 60 * 5, // Data stays fresh for 5 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
  });

  // Only show loading if we're fetching AND have no cached data
  const isLoading = queryLoading && !cachedData;

  const projects = data?.projects ?? [];
  const sharedProjects = data?.sharedProjects ?? [];

  // Pre-populate currentProject from cached data when initialProjectId is provided
  useEffect(() => {
    if (initialProjectId && !currentProject && projects.length > 0) {
      const project = projects.find(p => p.id === initialProjectId);
      if (project) {
        setCurrentProject(project);
      }
    }
  }, [initialProjectId, projects, currentProject]);

  const refreshProjects = useCallback(async () => {
    await refetch();
    if (currentProject) {
      if (user) {
        const updated = await dbStorage.getProject(currentProject.id);
        setCurrentProject(updated);
      } else {
        const updated = storage.getProject(currentProject.id);
        setCurrentProject(updated || null);
      }
    }
  }, [currentProject, user, refetch]);

  const createProject = useCallback(async (name: string): Promise<CreateProjectResult> => {
    if (!canCreateProject) {
      return { success: false, reason: 'project-limit' };
    }
    
    if (!user) {
      const project = storage.createProject(name);
      trackEvent('project_created', { project_name: name });
      await refreshProjects();
      await refreshUsage();
      setCurrentProject(project);
      return { success: true, project };
    }
    
    const project = await dbStorage.createProject(user.id, name);
    if (project) {
      trackEvent('project_created', { project_name: name });
      await refreshProjects();
      await refreshUsage();
      setCurrentProject(project);
      return { success: true, project };
    }
    return { success: false, reason: 'project-limit' };
  }, [refreshProjects, refreshUsage, user, canCreateProject]);

  const selectProject = useCallback(async (projectId: string) => {
    if (user) {
      const project = await dbStorage.getProject(projectId);
      setCurrentProject(project);
    } else {
      const project = storage.getProject(projectId);
      setCurrentProject(project || null);
    }
  }, [user]);

  const deleteProject = useCallback(async (projectId: string) => {
    if (user) {
      await dbStorage.deleteProject(projectId);
    } else {
      storage.deleteProject(projectId);
    }
    
    if (currentProject?.id === projectId) {
      setCurrentProject(null);
    }
    await refreshProjects();
  }, [currentProject, refreshProjects, user]);

  const updateProjectName = useCallback(async (projectId: string, name: string) => {
    if (user) {
      await dbStorage.updateProject(projectId, { name });
    } else {
      storage.updateProject(projectId, { name });
    }
    await refreshProjects();
  }, [refreshProjects, user]);

  const addRig = useCallback(async (
    rig: Omit<SavedRig, 'id' | 'createdAt' | 'updatedAt'>,
    projectId?: string
  ): Promise<AddRigResult> => {
    const targetProjectId = projectId || currentProject?.id;
    if (!targetProjectId) return { success: false, reason: 'rig-limit' };
    
    if (!canCreateRig(targetProjectId)) {
      return { success: false, reason: 'rig-limit' };
    }
    
    if (!user) {
      const newRig = storage.addRig(targetProjectId, rig);
      if (newRig) {
        trackEvent('rig_created', {
          rig_name: rig.name,
          camera_name: `${rig.camera.brand} ${rig.camera.name}`,
          rig_style: rig.rigStyle,
        });
      }
      await refreshProjects();
      await refreshUsage();
      return newRig ? { success: true, rig: newRig } : { success: false, reason: 'rig-limit' };
    }
    
    const newRig = await dbStorage.addRig(targetProjectId, rig);
    if (newRig) {
      trackEvent('rig_created', {
        rig_name: rig.name,
        camera_name: `${rig.camera.brand} ${rig.camera.name}`,
        rig_style: rig.rigStyle,
      });
    }
    
    await refreshProjects();
    await refreshUsage();
    return newRig ? { success: true, rig: newRig } : { success: false, reason: 'rig-limit' };
  }, [currentProject, refreshProjects, refreshUsage, user, canCreateRig]);

  const updateRig = useCallback(async (
    rigId: string, 
    updates: Partial<Omit<SavedRig, 'id' | 'createdAt'>>,
    projectId?: string
  ) => {
    const targetProjectId = projectId || currentProject?.id;
    if (!targetProjectId) return undefined;
    
    let updated: SavedRig | null | undefined;
    if (user) {
      updated = await dbStorage.updateRig(targetProjectId, rigId, updates);
    } else {
      updated = storage.updateRig(targetProjectId, rigId, updates);
    }
    
    await refreshProjects();
    return updated || undefined;
  }, [currentProject, refreshProjects, user]);

  const deleteRig = useCallback(async (rigId: string, projectId?: string) => {
    const targetProjectId = projectId || currentProject?.id;
    if (!targetProjectId) return false;
    
    let result: boolean;
    if (user) {
      result = await dbStorage.deleteRig(targetProjectId, rigId);
    } else {
      result = storage.deleteRig(targetProjectId, rigId);
    }
    
    await refreshProjects();
    return result;
  }, [currentProject, refreshProjects, user]);

  const duplicateRig = useCallback(async (rigId: string, projectId?: string): Promise<AddRigResult> => {
    const targetProjectId = projectId || currentProject?.id;
    if (!targetProjectId) return { success: false, reason: 'rig-limit' };
    
    if (!canCreateRig(targetProjectId)) {
      return { success: false, reason: 'rig-limit' };
    }
    
    let newRig: SavedRig | null | undefined;
    if (user) {
      newRig = await dbStorage.duplicateRig(targetProjectId, rigId);
    } else {
      newRig = storage.duplicateRig(targetProjectId, rigId);
    }
    
    await refreshProjects();
    await refreshUsage();
    
    return newRig ? { success: true, rig: newRig } : { success: false, reason: 'rig-limit' };
  }, [currentProject, refreshProjects, refreshUsage, user, canCreateRig]);

  return {
    projects,
    sharedProjects,
    currentProject,
    isLoading,
    createProject,
    selectProject,
    deleteProject,
    updateProjectName,
    addRig,
    updateRig,
    deleteRig,
    duplicateRig,
    setCurrentProject,
    refreshProjects,
    maxProjects,
    maxRigsPerProject,
  };
}
